import ModelAbstract from './ModelAbstract';
import FigureCollection from './FigureCollection';
import FigureImage from './FigureImage';
import CompanyFigure from './CompanyFigure';

class Figure extends ModelAbstract {
    constructor({
        id = null,
        name = '',
        company = [],
        category = null,
        classification = null,
        origin = null,
        character = null,
        material = null,
        artists = [],
        version = null,
        scale = null,
        height = null,
        width = null,
        length = null,
        unit = null,
        releaseDate = null,
        description = null,
        imageUrl = null,
        createdBy = null,
        createdByRole = 'user',
        approvedDate = null,
        approvedBy = null,
        approvedByRole = null,
        collections = [],
        figureImages = [],
        createdAt = null,
        updatedAt = null,
        // Additional frontend properties
        collectionStatus = null,
        tags = [],
        communityTags = [],
    } = {}) {
        super({ id, createdAt, updatedAt });
        this.name = name;
        this.company = company;
        this.category = category;
        this.classification = classification;
        this.origin = origin;
        this.character = character;
        this.material = material;
        this.artists = artists || [];
        this.version = version;
        this.scale = scale;
        this.height = height;
        this.width = width;
        this.length = length;
        this.unit = unit;
        this.releaseDate = releaseDate ? new Date(releaseDate) : null;
        this.description = description;
        this.imageUrl = imageUrl;
        this.createdBy = createdBy;
        this.createdByRole = createdByRole;
        this.approvedDate = approvedDate ? new Date(approvedDate) : null;
        this.approvedBy = approvedBy;
        this.approvedByRole = approvedByRole;
        this.collections = collections;
        this.figureImages = figureImages;

        // Frontend specific properties
        this.collectionStatus = collectionStatus;
        this.tags = tags || [];
        this.communityTags = communityTags || [];
    }

    /**
     * Factory method to create a Figure instance from API response
     * @param {Object} data - Raw figure data from API
     * @returns {Figure} - Instantiated Figure object
     */
    static fromAPI(data) {
        if (!data) return null;

        const collections = data.collections 
            ? data.collections.map(collection => FigureCollection.fromAPI(collection)) 
            : [];
            
        const figureImages = data.figureImages 
            ? data.figureImages.map(image => FigureImage.fromAPI(image)) 
            : [];

        const company = data.company 
            ? (Array.isArray(data.company) 
                ? data.company.map(companyFigure => CompanyFigure.fromAPI(companyFigure))
                : [])
            : [];

        const tags = Array.isArray(data.tags) ? data.tags : [];
        const communityTags = Array.isArray(data.communityTags) ? data.communityTags : [];
            
        return new Figure({
            id: data.id,
            name: data.name,
            company: company,
            category: data.category,
            classification: data.classification,
            origin: data.origin,
            character: data.character,
            material: data.material,
            artists: data.artists || [],
            version: data.version,
            scale: data.scale,
            height: data.height,
            width: data.width,
            length: data.length,
            unit: data.unit,
            releaseDate: data.releaseDate,
            description: data.description,
            imageUrl: data.imageUrl,
            createdBy: data.createdBy,
            createdByRole: data.createdByRole,
            approvedDate: data.approvedDate,
            approvedBy: data.approvedBy,
            approvedByRole: data.approvedByRole,
            collections: collections,
            figureImages: figureImages,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            tags,
            communityTags,
        });
    }

    /**
     * Get the main display image for this figure
     * @returns {string} URL to the main image
     */
    getMainImage() {
        if (this.figureImages && this.figureImages.length > 0) {
            return this.figureImages[0].url;
        }
        return this.imageUrl || null;
    }

    /**
     * Get all images for this figure
     * @returns {Array} Array of image URLs
     */
    getAllImages() {
        const images = [];
        
        // Add main image URL if it exists
        if (this.imageUrl) {
            images.push(this.imageUrl);
        }
        
        // Add all figure images
        if (this.figureImages && this.figureImages.length > 0) {
            this.figureImages.forEach(image => {
                images.push(image.url);
            });
        }
        
        return images;
    }

    /**
     * Get the company name(s) for this figure
     * @returns {string} Comma-separated list of company names
     */
    getCompanyNames() {
        if (!this.company || this.company.length === 0) {
            return '';
        }
        
        return this.company
            .map(companyFigure => companyFigure.company?.name || '')
            .filter(name => name !== '')
            .join(', ');
    }

    /**
     * Get dimensions as formatted string
     * @returns {string} Formatted dimensions
     */
    getDimensions() {
        const dimensions = [];
        
        if (this.height) dimensions.push(`H: ${this.height}`);
        if (this.width) dimensions.push(`W: ${this.width}`);
        if (this.length) dimensions.push(`L: ${this.length}`);
        
        if (dimensions.length === 0) return '';
        
        return `${dimensions.join(' x ')} ${this.unit || ''}`;
    }

    /**
     * Convert to plain object for JSON serialization
     */
    toJSON() {
        return {
            ...super.toJSON(),
            name: this.name,
            category: this.category,
            classification: this.classification,
            origin: this.origin,
            character: this.character,
            material: this.material,
            artists: this.artists,
            version: this.version,
            scale: this.scale,
            height: this.height,
            width: this.width,
            length: this.length,
            unit: this.unit,
            releaseDate: this.releaseDate?.toISOString() || null,
            description: this.description,
            imageUrl: this.imageUrl,
            collectionStatus: this.collectionStatus,
            tags: this.tags,
            communityTags: this.communityTags,
        };
    }
}

export default Figure;
